/**
 * useResultsEditing Hook
 *
 * Manages edit mode state, pending changes, and save/discard
 * for query editor results. Only supports UPDATE operations.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { QueryColumn } from '../../../../types/queryExecution';
import type { PendingUpdate } from '../../core/types/workspaceTypes';
import { detectColumnEditability, type ColumnEditability } from '../utils/editabilityDetection';
import { applyResultsEdits } from '../services/resultsEditingApi';
import { useLookupCacheWriteThrough } from '../../common/hooks/useLookupCacheWriteThrough';
import { toast } from '../../../../components/ui/toaster';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { transactionSessionClaimForTab } from '../utils/transactionSessionClaim';
import { releaseTabSubmission, tryClaimTabSubmission } from '../services/tabSubmissionGate';

type UseResultsEditingParams = {
  columns: QueryColumn[];
  connectionId?: string;
  database?: string;
  /** Tab ID — used to route saves through an active transaction session. */
  tabId?: string;
  /** Connection engine — only needed for the post-save lookup cache refresh. */
  engine?: string;
  /** Called after successfully saving changes — triggers query re-execution */
  onSaveComplete?: () => void;
  /**
   * The tab's transaction auto-begin, run before a Save as Execute runs it
   * before a statement: resolves false when the Save must not proceed.
   */
  prepareForSave?: () => Promise<boolean>;
};

type UseResultsEditingResult = {
  // Editability info
  editability: ColumnEditability;
  hasEditableColumns: boolean;
  /** Why editing is unavailable (only set when hasEditableColumns is false) */
  readOnlyReason?: string;

  // Edit mode state
  isEditMode: boolean;
  pendingChanges: PendingUpdate[];
  pendingChangeCount: number;
  isSaving: boolean;

  // Actions
  toggleEditMode: () => void;
  enterEditMode: () => void;
  exitEditMode: () => void;
  discardChanges: () => void;
  saveChanges: () => Promise<void>;

  // Cell-level helpers
  isColumnEditable: (columnName: string) => boolean;
  getCellPendingValue: (rowPk: Record<string, unknown>, field: string) => unknown | undefined;
  getPrimaryKeyForCell: (row: Record<string, unknown>, columnName: string) => Record<string, unknown> | null;
  addCellUpdate: (row: Record<string, unknown>, columnName: string, oldValue: unknown, newValue: unknown) => void;
};

/**
 * Compare two primary key objects for equality
 */
function primaryKeysMatch(pk1: Record<string, unknown>, pk2: Record<string, unknown>): boolean {
  const keys1 = Object.keys(pk1).sort();
  const keys2 = Object.keys(pk2).sort();
  if (keys1.length !== keys2.length) return false;
  if (keys1.some((k, i) => k !== keys2[i])) return false;
  return keys1.every((key) => pk1[key] === pk2[key]);
}

export function useResultsEditing({
  columns,
  connectionId,
  database,
  tabId,
  engine,
  onSaveComplete,
  prepareForSave,
}: UseResultsEditingParams): UseResultsEditingResult {
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<PendingUpdate[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Task 5.2 — a results edit is a real row mutation, so it refreshes any
  // lookup enum whose labels are read from the table it landed on.
  const refreshLookupCachesForEditedTable = useLookupCacheWriteThrough({
    connectionId,
    database,
    engine,
  });

  // When results/context changes (new query, different connection/database), pending edits
  // are no longer valid. Reset edit mode to avoid leaking state across result sets.
  // Use functional updater for setPendingChanges to return the same reference when already empty,
  // preventing re-renders that would otherwise create an infinite loop when `columns` is a fresh `[]`.
  useEffect(() => {
    setIsEditMode(false);
    setPendingChanges(prev => prev.length === 0 ? prev : []);
  }, [columns, connectionId, database]);

  // Compute editability from columns
  const editability = useMemo(
    () => detectColumnEditability(columns),
    [columns]
  );

  const hasEditableColumns = editability.hasEditableColumns;

  // Actions
  const enterEditMode = useCallback(() => {
    if (!hasEditableColumns) return;
    setIsEditMode(true);
  }, [hasEditableColumns]);

  const exitEditMode = useCallback(() => {
    setIsEditMode(false);
    setPendingChanges([]);
  }, []);

  const toggleEditMode = useCallback(() => {
    if (isEditMode) {
      // If there are pending changes, don't auto-exit (caller should show dialog)
      if (pendingChanges.length > 0) return;
      exitEditMode();
    } else {
      enterEditMode();
    }
  }, [isEditMode, pendingChanges.length, enterEditMode, exitEditMode]);

  const discardChanges = useCallback(() => {
    setPendingChanges([]);
    setIsEditMode(false);
  }, []);

  const saveChanges = useCallback(async () => {
    if (!connectionId || !database || pendingChanges.length === 0) return;
    // Same one-at-a-time gate as Execute (`tabSubmissionGate.ts`): a Save
    // while the tab's Execute or another Save is starting is ignored.
    const claim = tabId ? tryClaimTabSubmission(tabId) : null;
    if (tabId && !claim) return;
    const releaseGate = () => {
      if (tabId && claim) releaseTabSubmission(tabId, claim);
    };

    setIsSaving(true);
    try {
      // A Save is a write, so it goes through the same auto-begin as Execute:
      // a Manual-mode (or Smart-mode) tab with no transaction begins one on
      // its session first, and a begin that fails — inside a reconnect, say —
      // refuses the Save (toast shown by the begin), pending edits kept.
      // Without this an idle tab's Save carried no claim and ran in auto-commit.
      if (prepareForSave && !(await prepareForSave())) return;
      // Same claim query execution sends: a Save from a tab in a transaction
      // must land on ITS session or be refused — never applied in auto-commit.
      const claim = tabId ? transactionSessionClaimForTab(useWorkspaceStore.getState().tabs, tabId) : {};
      const applying = applyResultsEdits({
        connectionId,
        database,
        pendingChanges,
        editableColumns: editability.editableColumns,
        editableTables: editability.editableTables,
        tabId,
        ...claim,
      });
      // Dispatched; `isSaving` guards the rest.
      releaseGate();
      const result = await applying;

      if (result.code === 'TRANSACTION_SESSION_MISSING' && tabId) {
        // The tab's transaction is gone (a teardown released it): reset the
        // toolbar the same way a refused statement does, and say so.
        useWorkspaceStore.getState().updateTabContent(tabId, {
          transactionState: 'idle',
          transactionSessionGeneration: undefined,
        });
      }

      if (result.success) {
        toast.success({
          title: 'Changes saved',
          description: `Updated ${result.tablesUpdated} table${result.tablesUpdated !== 1 ? 's' : ''}`,
        });
        for (const updated of result.updatedTables) {
          refreshLookupCachesForEditedTable(updated);
        }
        setPendingChanges([]);
        setIsEditMode(false);
        onSaveComplete?.();
      } else {
        toast.error({
          title: 'Save failed',
          description: result.error || 'Unknown error',
        });
      }
    } catch (error) {
      toast.error({
        title: 'Save failed',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      releaseGate();
      setIsSaving(false);
    }
  }, [
    connectionId,
    database,
    pendingChanges,
    editability,
    tabId,
    onSaveComplete,
    prepareForSave,
    refreshLookupCachesForEditedTable,
  ]);

  // Cell-level helpers
  const isColumnEditable = useCallback(
    (columnName: string) => editability.editableColumns.has(columnName),
    [editability]
  );

  const getCellPendingValue = useCallback(
    (rowPk: Record<string, unknown>, field: string): unknown | undefined => {
      const change = pendingChanges.find(
        (c) => c.field === field && primaryKeysMatch(c.primaryKey, rowPk)
      );
      return change ? change.newValue : undefined;
    },
    [pendingChanges]
  );

  const getPrimaryKeyForCell = useCallback(
    (row: Record<string, unknown>, columnName: string): Record<string, unknown> | null => {
      const colInfo = editability.editableColumns.get(columnName);
      if (!colInfo) return null;

      const tableKey = `${colInfo.schema}.${colInfo.table}`;
      const tableInfo = editability.editableTables.get(tableKey);
      if (!tableInfo) return null;

      // Build PK from the row data using the PK column names
      // PK columns in the result may have different names (aliases)
      // We need to find them by matching sourceColumn to PK column names
      const pk: Record<string, unknown> = {};
      for (const pkCol of tableInfo.pkColumns) {
        // Find the result column that maps to this PK source column
        const resultCol = columns.find(
          (c) =>
            c.sourceSchema === tableInfo.schema &&
            c.sourceTable === tableInfo.table &&
            c.sourceColumn === pkCol
        );
        if (!resultCol) return null; // PK column not found in result — shouldn't happen
        const value = row[resultCol.id ?? resultCol.name];
        if (value === undefined) return null;
        // Primary keys are stored using SOURCE column names (not result aliases),
        // because the table explorer change generators expect real table columns.
        pk[pkCol] = value;
      }

      return pk;
    },
    [editability, columns]
  );

  const addCellUpdate = useCallback(
    (row: Record<string, unknown>, columnName: string, oldValue: unknown, newValue: unknown) => {
      const pk = getPrimaryKeyForCell(row, columnName);
      if (!pk) return;

      // Don't add if values are the same
      if (oldValue === newValue) return;

      setPendingChanges((prev) => {
        // Check if there's already an update for this cell
        const existingIndex = prev.findIndex(
          (c) => c.field === columnName && primaryKeysMatch(c.primaryKey, pk)
        );

        if (existingIndex >= 0) {
          const existing = prev[existingIndex];
          // If new value equals original, remove the change
          if (existing.oldValue === newValue) {
            return prev.filter((_, i) => i !== existingIndex);
          }
          // Update to new value
          return prev.map((c, i) => (i === existingIndex ? { ...c, newValue } : c));
        }

        // Add new change
        return [...prev, { type: 'update' as const, primaryKey: pk, field: columnName, oldValue, newValue }];
      });
    },
    [getPrimaryKeyForCell]
  );

  return {
    editability,
    hasEditableColumns,
    readOnlyReason: editability.readOnlyReason,
    isEditMode,
    pendingChanges,
    pendingChangeCount: pendingChanges.length,
    isSaving,
    toggleEditMode,
    enterEditMode,
    exitEditMode,
    discardChanges,
    saveChanges,
    isColumnEditable,
    getCellPendingValue,
    getPrimaryKeyForCell,
    addCellUpdate,
  };
}
